/**
 * assets.ts — Central runtime asset registry.
 *
 * Single source of truth for every static asset path served from Public/Assets/.
 * All runtime components must reference paths through this module.
 *
 * NOT included here (managed separately):
 *   - Tile face/back paths       → src/config/dominoAssets.ts
 *   - Tile skin folder paths     → src/config/tileSkin.ts
 */

const BASE = '/Assets';

export const ASSETS = {
  brand: {
    ICON:             `${BASE}/brand/the-domino-game/icon.png`,
    LOGO_HORIZONTAL:  `${BASE}/brand/the-domino-game/logo-horizontal.png`,
    LOGO_STACKED:     `${BASE}/brand/the-domino-game/logo-stacked.png`,
    WORDMARK:         `${BASE}/brand/the-domino-game/wordmark.png`,
    /** DS2 approved Figma export — sidebar lockup (210×234, near-square). */
    LOGO_SIDEBAR:     `${BASE}/brand/the-domino-game/logo-sidebar.svg`,
    /** DS2 approved Figma export — footer lockup (334.8×145.2, wide). */
    LOGO_FOOTER:      `${BASE}/brand/the-domino-game/logo-footer.svg`,
  },

  /** DS2 approved Figma export — Sidebar navigation icon set (800×800 RGBA source). */
  navigation: {
    PLAY_ONLINE: `${BASE}/shell/icons/play-online.png`,
    PLAY_BOTS:   `${BASE}/shell/icons/play-bots.png`,
    LEADERBOARD: `${BASE}/shell/icons/leaderboard.png`,
    FRIENDS:     `${BASE}/shell/icons/friends.png`,
    RULES:       `${BASE}/shell/icons/rules.png`,
    SETTINGS:    `${BASE}/shell/icons/settings.png`,
  },

  /** DS2 approved Figma export — Get Started auth-provider icons (48×48 SVG).
      Apple is a white glyph on transparent — dark backgrounds only.
      Email/sms icon not yet delivered — do not substitute. */
  auth: {
    GOOGLE:   `${BASE}/shell/icons/auth-google.svg`,
    APPLE:    `${BASE}/shell/icons/auth-apple.svg`,
    FACEBOOK: `${BASE}/shell/icons/auth-facebook.svg`,
  },

  /** DS2 approved Figma export — Footer social icons (53×53 RGBA source, white glyph on transparent). */
  social: {
    FACEBOOK:  `${BASE}/shell/icons/social-facebook.png`,
    INSTAGRAM: `${BASE}/shell/icons/social-instagram.png`,
    YOUTUBE:   `${BASE}/shell/icons/social-youtube.png`,
    TIKTOK:    `${BASE}/shell/icons/social-tiktok.png`,
  },

  board: {
    // Production board artwork. Points at table-green.png (premium wood +
    // leather table). table-classic.png remains on disk, unreferenced, for
    // rollback — see git history for the previous value of this constant.
    TABLE_CLASSIC: `${BASE}/games/dominoes/board/table-green.png`,
    TABLE_FELT:    `${BASE}/games/dominoes/board/table-felt.png`,
    FELT_GREEN:    `${BASE}/games/dominoes/board/felt-green.png`,
    /** DS2 approved Figma export — Play Online lobby table thumbnail (1500² wood+felt, no logo). */
    TABLE_LOBBY:   `${BASE}/games/dominoes/board/table-lobby.png`,
  },

  dominoesUi: {
    LOGO:       `${BASE}/games/dominoes/ui/logo.png`,
    KNOCK_FIST: `${BASE}/games/dominoes/ui/knock-fist.png`,
  },

  shellAudio: {
    ACCEPT:       `${BASE}/game/audio/app/accept.mp3`,
    DENY:         `${BASE}/game/audio/app/deny.mp3`,
    HOVER:        `${BASE}/game/audio/app/hover.mp3`,
    NOTIFICATION: `${BASE}/game/audio/app/notification.mp3`,
    CLICK: [
      `${BASE}/game/audio/app/click_01.mp3`,
      `${BASE}/game/audio/app/click_02.mp3`,
      `${BASE}/game/audio/app/click_03.mp3`,
    ] as const,
  },

  gameAudio: {
    TILE_SELECT:       `${BASE}/game/audio/game/tile_select.mp3`,
    TURN_INDICATOR:    `${BASE}/game/audio/game/turn_indicator.mp3`,
    GAME_RESULT_WIN:   `${BASE}/game/audio/game/game_result_win.mp3`,
    GAME_RESULT_LOSE:  `${BASE}/game/audio/game/game_result_lose.mp3`,
    SESSION_END_WIN:   `${BASE}/game/audio/game/session_end_win.mp3`,
    SESSION_END_LOSE:  `${BASE}/game/audio/game/session_end_lose.mp3`,
    TILE_PLACE: [
      `${BASE}/game/audio/game/tile_place1.mp3`,
      `${BASE}/game/audio/game/tile_place2.mp3`,
      `${BASE}/game/audio/game/tile_place3.mp3`,
    ] as const,
    PASS: [
      `${BASE}/game/audio/game/pass1.mp3`,
      `${BASE}/game/audio/game/pass2.mp3`,
    ] as const,
  },

  marketing: {
    /** DS2 approved Figma export — Home hero board photo (1350×1350, top-down branded table). */
    HERO_TDG: `${BASE}/marketing/home/hero-tdg.png`,
    LANDING_BANNER: `${BASE}/marketing/dominos-banner.jpg`,
    /** DS2 approved Figma export — Landing hero visual (empty branded table, 1350×1350 source). */
    HERO_BOARD_1: `${BASE}/marketing/landing/hero-board-1.png`,
    /** DS2 approved Figma export — "Rise. Compete. Connect." visual (populated gameplay scene, 1495×1495 source). */
    HERO_BOARD_2: `${BASE}/marketing/landing/hero-board-2.png`,
    /** DS2 approved Figma export — Play Online QUICK MATCH banner domino render (1600×1000). */
    LOBBY_BANNER: `${BASE}/marketing/lobby/banner-domino.png`,
  },
} as const;
